import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Download } from "lucide-react";
import { toast } from "sonner";
import api, { download, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApi, PageHeader, Loading, Empty, Pager, TableWrap, Field, Select } from "../components/common";
import { useDirectory } from "../components/LeadForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { fdate } from "../lib/format";

const EMPTY = { name: "", studio_name: "", phone: "", whatsapp: "", email: "", address: "", city: "", source: "", assigned_to: "", notes: "" };

export function ClientForm({ open, onClose, initial, onSaved }) {
  const { user, settings } = useAuth();
  const sales = useDirectory("sales");
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY); }, [open, initial]);
  const s = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const save = async () => {
    try {
      const body = { ...f, email: f.email || null, assigned_to: f.assigned_to || null };
      const r = initial?.id ? await api.put(`/clients/${initial.id}`, body) : await api.post("/clients", body);
      toast.success("Client saved");
      onSaved?.(r.data);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="client-form-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {[["name", "Client name *"], ["studio_name", "Studio name"], ["phone", "Phone *"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["city", "City"]].map(([k, l]) => (
            <Field key={k} label={l}><input data-testid={`client-${k}`} className="vmw-input" value={f[k] || ""} onChange={s(k)} /></Field>))}
          <Field label="Address" className="sm:col-span-2"><input data-testid="client-address" className="vmw-input" value={f.address || ""} onChange={s("address")} /></Field>
          <Field label="Lead source"><Select testid="client-source" placeholder="—" options={settings?.lead_sources || []} value={f.source} onChange={s("source")} /></Field>
          {user.role === "owner" && <Field label="Assigned salesperson"><Select testid="client-assigned" placeholder="Unassigned" options={sales.map((x) => ({ value: x.id, label: x.name }))} value={f.assigned_to} onChange={s("assigned_to")} /></Field>}
          <Field label="Notes" className="sm:col-span-2"><textarea className="vmw-input h-16 py-2" value={f.notes || ""} onChange={s("notes")} /></Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="vmw-gradient" onClick={save} data-testid="client-form-submit">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const { data, loading, reload } = useApi(`/clients?q=${encodeURIComponent(q)}&page=${page}`);
  return (
    <div>
      <PageHeader title="Clients" subtitle="Studios and couples you work with" actions={<>
        <Button variant="outline" onClick={() => download("/export/clients", "clients.csv")} data-testid="client-export-button"><Download className="h-4 w-4 mr-1" />Export</Button>
        <Button className="vmw-gradient" onClick={() => setOpen(true)} data-testid="client-add-button"><Plus className="h-4 w-4 mr-1" />New Client</Button></>} />
      <input data-testid="client-search" className="vmw-input max-w-sm mb-4" placeholder="Search clients…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
      <TableWrap>
        {loading && !data ? <Loading /> : !data?.items.length ? <Empty text="No clients yet. Clients are created when a deal is confirmed." /> : (
          <table className="vmw-table" data-testid="clients-table">
            <thead><tr><th>Client ID</th><th>Name</th><th>Studio</th><th>Phone</th><th>City</th><th>Source</th><th>Since</th></tr></thead>
            <tbody>{data.items.map((c) => (
              <tr key={c.id} data-testid={`client-row-${c.id}`}>
                <td><Link to={`/clients/${c.id}`} className="font-mono text-xs font-semibold text-primary">{c.code}</Link></td>
                <td><Link to={`/clients/${c.id}`} className="font-medium hover:text-primary">{c.name}</Link></td>
                <td>{c.studio_name}</td><td>{c.phone}</td><td>{c.city}</td><td>{c.source}</td><td>{fdate(c.created_date)}</td>
              </tr>))}</tbody>
          </table>)}
        {data && <Pager page={page} total={data.total} pageSize={25} onPage={setPage} />}
      </TableWrap>
      <ClientForm open={open} onClose={() => setOpen(false)} onSaved={reload} />
    </div>
  );
}
